// WordPress writes this cookie on .seatow.com immediately before a full-page
// navigation into the app. The click never reaches UXCam on seatow.com because
// that document unloads first. Names and click_location values are the
// cross-domain rows from the GTM sheet. This app's own events (page_viewed,
// payment_failed, …) are not accepted from the cookie.

import { isPlainObject } from 'lodash'
import { isAnalyticsAllowed } from 'consent-service'

const COOKIE_NAME = 'seatow_uxcam_cross_domain_events'

const MAX_AGE_MS = 60 * 1000
// Written in the browser immediately before navigation, so this normally matches
// the device clock. A few seconds of slack still accepts a stamp slightly ahead
// of us, and rejects anything further in the future so a tampered `created`
// cannot stay fresh past the 60-second window.
const CLOCK_SKEW_MS = 5 * 1000
const MAX_COOKIE_LENGTH = 4096
const MAX_EVENTS = 10

const CLICK_LOCATIONS = ['Header', 'Footer', 'Why-Sea-Tow', 'Other']

// An empty list means the event is sent with no properties. click_location is
// the only property forwarded, and only for the three conversion events.
const ALLOWED_EVENTS = {
	'Click Become-a-Member': CLICK_LOCATIONS,
	'Click Join': CLICK_LOCATIONS,
	'Click Renew': CLICK_LOCATIONS,
	'Click Login-or-Register': [],
	'Click Join--Gold-STCard-Card': [],
	'Click Join--Lake-Card-STCard-Card': [],
}

const CLEAR_COOKIE = `${COOKIE_NAME}=; Max-Age=0; Path=/; Domain=.seatow.com; Secure; SameSite=Lax`

// Same id `loadSdk` uses. The remote script replaces the stub that queues on `__t`.
const SDK_SCRIPT_ID = 'uxcam-web-sdk'

function isFresh(created, now) {
	return (
		typeof created === 'number' &&
		Number.isFinite(created) &&
		created <= now + CLOCK_SKEW_MS &&
		now - created < MAX_AGE_MS
	)
}

function readCookie() {
	const prefix = `${COOKIE_NAME}=`
	const item = document.cookie
		.split(';')
		.map((part) => part.trim())
		.find((part) => part.startsWith(prefix))

	if (!item) return null

	// Keep an empty value distinct from a missing cookie so a blank handoff
	// can be deleted instead of left in place.
	return item.slice(prefix.length)
}

function clearCookie() {
	document.cookie = CLEAR_COOKIE
}

function isQueueingStub(sdk) {
	return Array.isArray(sdk?.__t)
}

// A logged-out Renew or Login lands on a route that redirects to Auth0. That
// navigation can unload the page while the click is only queued on the stub.
// Keep the cookie until the remote script has replaced the stub, so the next
// page can send it if this one dies first.
function clearAfterSdkAcceptsQueue() {
	if (!isQueueingStub(window.uxc)) {
		clearCookie()
		return
	}

	const script = document.getElementById(SDK_SCRIPT_ID)
	if (!script) return

	script.addEventListener(
		'load',
		() => {
			if (!isQueueingStub(window.uxc)) clearCookie()
		},
		{ once: true },
	)
}

// `dispose` means the cookie can never produce a send, so the caller deletes
// it. A fresh, allowlisted payload is kept until consent and the SDK can take it.
function selectCrossDomainEvents(raw, now = Date.now()) {
	if (typeof raw !== 'string' || raw.length > MAX_COOKIE_LENGTH) {
		return { dispose: true, events: [] }
	}

	let parsed
	try {
		parsed = JSON.parse(decodeURIComponent(raw))
	} catch {
		return { dispose: true, events: [] }
	}

	if (!Array.isArray(parsed) || parsed.length > MAX_EVENTS) {
		return { dispose: true, events: [] }
	}

	const events = []

	parsed.forEach((event) => {
		if (!isPlainObject(event) || !isFresh(event.created, now)) return

		// `toString` and `constructor` exist on the prototype. A name that is not a
		// string, such as ["Click Join"], would also stringify into an allowlisted
		// key and then be sent as an array.
		if (typeof event.name !== 'string') return
		if (!Object.hasOwn(ALLOWED_EVENTS, event.name)) return

		const locations = ALLOWED_EVENTS[event.name]

		const clickLocation = isPlainObject(event.properties)
			? event.properties.click_location
			: undefined

		events.push({
			name: event.name,
			properties: locations.includes(clickLocation)
				? { click_location: clickLocation }
				: {},
		})
	})

	return { dispose: events.length === 0, events }
}

// Returns whether anything was handed to UXCam. A valid cookie is left in place
// when consent or the SDK is not ready yet, so a later grant can still send it.
export function flushCrossDomainEvents(send) {
	const raw = readCookie()
	if (raw === null) return false

	const selected = selectCrossDomainEvents(raw)

	if (selected.dispose) {
		clearCookie()
		return false
	}

	if (
		!isAnalyticsAllowed() ||
		typeof send !== 'function' ||
		typeof window.uxc?.event !== 'function'
	) {
		return false
	}

	selected.events.forEach(({ name, properties }) => send(name, properties))
	clearAfterSdkAcceptsQueue()
	return true
}

export function discardCrossDomainEvents() {
	clearCookie()
}
